import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AumFinancialNumberPipe, DateLocalePipe, ReportType } from '@modeso/aumico-lib-core-fe';
import {
    IAnnualReport,
    IAnnualReportPdfRequest,
    IFinStatements,
    ISetupData,
} from '@modeso/types__aumico-lib-annualreport-be';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import Debug from 'debug';
import _ from 'lodash';
import { EMPTY, interval, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, take, takeUntil, tap, timeout, withLatestFrom } from 'rxjs/operators';
import { NotificationsSocketService } from '../../../../aumico-lib-core-fe/src/lib/services/notifications-socket.service';
import * as fromActions from '../actions/annualReport.actions';
import { AnnualReportMetaInformationDTO } from '../dtos/annualReportMetaInformation.dto';
import { CreateAnnualReportResponseDto } from '../dtos/create-annual-report-response.dto';
import { PrintUsingPdfTronDto } from '../dtos/print-using-pdf-tron.dto';
import { PlaceholderHelper } from '../helpers/placeholder.helper';
import { AnnualReportExcelSheet } from '../interfaces/export-excel/annual-report-excel-sheet.interface';
import { IPdfTronData } from '../interfaces/pdf-tron/pdf-tron-data.interface';
import { selectActiveAnnualReport } from '../reducer/annualReport.reducer';
import { AumicoAnnualReportService } from '../services/annualReport.service';

const debug = Debug('aumico:aumico-lib-annualreport-fe:AnnualReportEffects');

@Injectable()
export class AnnualReportEffects {
    constructor(
        private actions$: Actions,
        private service: AumicoAnnualReportService,
        private readonly store: Store,
        private readonly dateLocalePipe: DateLocalePipe,
        private readonly finNumberPipe: AumFinancialNumberPipe,
        private readonly notificationsSocketService: NotificationsSocketService,
    ) { }

    private generateFilename(data: IPdfTronData | null, extension: string): string {
        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD format

        if (!data || !data.customer || !data.cover) {
            // Fallback to timestamp-based name if data is not available
            return `AnnualReport_${today}.${extension}`;
        }

        const companyName = (data.customer.companyName || 'Company')
            .replace(/[^a-zA-Z0-9]/g, '_') // Replace non-alphanumeric characters with underscores
            .replace(/_+/g, '_') // Replace multiple underscores with single underscore
            .replace(/^_|_$/g, ''); // Remove leading/trailing underscores

        const reportingPeriod = (data.cover.reportingPeriod || 'Period')
            .replace(/[^a-zA-Z0-9]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');

        return `${companyName}_${reportingPeriod}_${today}.${extension}`;
    }

    createAnnualReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.createAnnualReport.type),
            mergeMap(
                (
                    action: fromActions.ActionWithPayload<{
                        setupData: ISetupData;
                        existingAnnualReportEncryptedId?: string;
                        existingProjectId?: string;
                    }>,
                ) => {
                    return this.service.createAnnualReport$(action.payload).pipe(
                        map((response: CreateAnnualReportResponseDto) => {
                            debug(response);
                            return fromActions.onCreateAnnualReportSuccessfully({
                                payload: response,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(fromActions.onCreateAnnualReportFailed({ payload: error }));
                        }),
                    );
                },
            ),
        ),
    );

    updateAnnualReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateAnnualReport.type),
            mergeMap(
                (
                    action: fromActions.ActionWithPayload<{
                        annualReport: IAnnualReport;
                        annualReportEncryptedId: string;
                    }>,
                ) => {
                    return this.service.updateAnnualReport$(action.payload).pipe(
                        concatMap(() => {
                            const placeholder = PlaceholderHelper.getPlaceholderMap(
                                action.payload.annualReport,
                                this.dateLocalePipe,
                                this.finNumberPipe,
                            );
                            return [
                                fromActions.onUpdateAnnualReportSuccessfully({
                                    payload: {
                                        annualReport: { ...action.payload.annualReport },
                                    },
                                }),
                                fromActions.updateAnnualReportPlaceHolder({
                                    payload: placeholder,
                                }),
                            ];
                        }),
                        catchError((error) => {
                            return of(fromActions.onUpdateAnnualReportFailed({ payload: error }));
                        }),
                    );
                },
            ),
        ),
    );
    enforceStateUpdateForAnnualReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.enforceStateUpdateForAnnualReport.type),
            // switchMap (or mergeMap) allows you to return an array of actions
            switchMap((action: fromActions.ActionWithPayload<{ annualReport: IAnnualReport }>) => {
                // 1. Calculate the placeholders locally
                const placeholder = PlaceholderHelper.getPlaceholderMap(
                    action.payload.annualReport,
                    this.dateLocalePipe,
                    this.finNumberPipe,
                );

                // 2. Return an array of actions to be dispatched immediately
                return [
                    fromActions.updateAnnualReportPlaceHolder({
                        payload: placeholder,
                    }),
                ];
            })
        )
    );

    updateFinStatementsOfAnnualReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateFinStatementsOfAnnualReport.type),
            withLatestFrom(this.store.select(selectActiveAnnualReport)),
            mergeMap(
                ([action, activeAnnualReport]: [
                    fromActions.ActionWithPayload<{ finStatements: IFinStatements[]; annualReportEncryptedId: string }>,
                    IAnnualReport | null,
                ]) => {
                    const newAnnualReport = _.cloneDeep(activeAnnualReport);
                    // we update the fcp output fin statements if the fcp is already set because
                    // we actually want to modify the finstatements after the fcp has been already been done
                    // in case the fcp is not set we just update the fin statements because in this case
                    // the FCP is disabled.
                    if (newAnnualReport?.fcp === null) {
                        newAnnualReport.finStatements = action.payload.finStatements;
                    } else {
                        newAnnualReport.fcp.outputFinStatements = action.payload.finStatements;
                    }

                    return this.service
                        .updateAnnualReport$({
                            annualReport: newAnnualReport,
                            annualReportEncryptedId: action.payload.annualReportEncryptedId,
                        })
                        .pipe(
                            concatMap(() => {
                                const placeholder = PlaceholderHelper.getPlaceholderMap(
                                    newAnnualReport,
                                    this.dateLocalePipe,
                                    this.finNumberPipe,
                                );
                                return [
                                    fromActions.onUpdateAnnualReportSuccessfully({
                                        payload: {
                                            annualReport: { ...newAnnualReport },
                                        },
                                    }),
                                    fromActions.updateAnnualReportPlaceHolder({
                                        payload: placeholder,
                                    }),
                                ];
                            }),
                            catchError((error) => {
                                return of(fromActions.onUpdateAnnualReportFailed({ payload: error }));
                            }),
                        );
                },
            ),
        ),
    );

    printAnnualReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.printAnnualReport.type),
            mergeMap((action: fromActions.ActionWithPayload<IAnnualReportPdfRequest>) => {
                return this.service.printAnnualReport$(action.payload).pipe(
                    map((response) => {
                        debug(response);
                        const blob = new Blob([response], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);

                        return fromActions.onPrintAnnualReportSuccessfully({ payload: { response, url } });
                    }),
                    catchError((error) => {
                        return of(fromActions.onPrintAnnualReportFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    printAnnualReportUsingPdfTron$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.printAnnualReportUsingPdfTron.printAnnualReportUsingPdfTron.type),
            mergeMap((action: fromActions.ActionWithPayload<PrintUsingPdfTronDto>) => {
                return this.service.printAnnualReportUsingPdfTron$(action.payload).pipe(
                    map((response) => {
                        const blob = new Blob([response], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const filename = this.generateFilename(action.payload.data, 'pdf');

                        const downloadLink = document.createElement('a');
                        downloadLink.href = window.URL.createObjectURL(blob);
                        downloadLink.download = this.generateFilename(action.payload.data, 'pdf'); // Remove this
                        downloadLink.click();

                        setTimeout(() => {
                            window.URL.revokeObjectURL(downloadLink.href);
                        }, 100);
                        return fromActions.printAnnualReportUsingPdfTron.printAnnualReportUsingPdfTronSuccess({
                            payload: { response, url, filename },
                        });
                    }),
                    catchError((error) => {
                        return of(
                            fromActions.printAnnualReportUsingPdfTron.printAnnualReportUsingPdfTronFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    printAnnualReportUsingPdfTronV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.printAnnualReportUsingPdfTronV2.printAnnualReportUsingPdfTronV2.type),
            mergeMap((action: fromActions.ActionWithPayload<PrintUsingPdfTronDto>) => {
                return this.service.printAnnualReportUsingPdfTronV2$(action.payload).pipe(
                    mergeMap(() => {
                        // HTTP 202 accepted, now wait for WebSocket notification
                        return this.notificationsSocketService.printJobCompleted$.pipe(
                            take(1),
                            timeout(60000),
                            map((payload) => {
                                if (payload.success && payload.data) {
                                    // Convert base64 to blob and create URL for PDF display
                                    const byteCharacters = atob(payload.data);
                                    const byteNumbers = new Array(byteCharacters.length);
                                    for (let i = 0; i < byteCharacters.length; i++) {
                                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                                    }
                                    const byteArray = new Uint8Array(byteNumbers);
                                    const blob = new Blob([byteArray], { type: 'application/pdf' });
                                    const pdfUrl = window.URL.createObjectURL(blob);

                                    // Also trigger download
                                    const downloadLink = document.createElement('a');
                                    downloadLink.href = pdfUrl;
                                    downloadLink.download = this.generateFilename(action.payload.data, 'pdf');
                                    downloadLink.click();

                                    return fromActions.printAnnualReportUsingPdfTronV2.printAnnualReportUsingPdfTronV2Success({
                                        payload: { pdfUrl }
                                    });
                                } else {
                                    throw new Error(payload.error || 'Failed to generate PDF');
                                }
                            }),
                            catchError((error) => {
                                return of(
                                    fromActions.printAnnualReportUsingPdfTronV2.printAnnualReportUsingPdfTronV2Failed({
                                        payload: error,
                                    }),
                                );
                            }),
                        );
                    }),
                    catchError((error) => {
                        return of(
                            fromActions.printAnnualReportUsingPdfTronV2.printAnnualReportUsingPdfTronV2Failed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    exportAnnualReportExcel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.exportAnnualReportExcel.exportAnnualReportExcel.type),
            mergeMap((action: fromActions.ActionWithPayload<{ sheetsData: AnnualReportExcelSheet[] }>) => {
                return this.service.exportAnnualReportExcel$(action.payload).pipe(
                    map((response) => {
                        const blob = new Blob([response], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        });
                        const downloadLink = document.createElement('a');
                        downloadLink.href = window.URL.createObjectURL(blob);
                        downloadLink.download = this.generateFilename(null, 'xlsx');
                        downloadLink.click();
                        return fromActions.exportAnnualReportExcel.exportAnnualReportExcelSuccess();
                    }),
                    catchError((error) => {
                        return of(
                            fromActions.exportAnnualReportExcel.exportAnnualReportExcelFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    exportAnnualReportExcelV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.exportAnnualReportExcelV2.exportAnnualReportExcelV2.type),
            mergeMap((action: fromActions.ActionWithPayload<{ sheets: AnnualReportExcelSheet[] }>) => {
                return this.service.exportAnnualReportExcelV2$(action.payload).pipe(
                    mergeMap(() => {
                        // HTTP 202 accepted, now wait for WebSocket notification
                        return this.notificationsSocketService.printJobCompleted$.pipe(
                            take(1),
                            timeout(60000),
                            map((payload) => {
                                if (payload.success && payload.data) {
                                    // Convert base64 to blob and download
                                    const byteCharacters = atob(payload.data);
                                    const byteNumbers = new Array(byteCharacters.length);
                                    for (let i = 0; i < byteCharacters.length; i++) {
                                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                                    }
                                    const byteArray = new Uint8Array(byteNumbers);
                                    const blob = new Blob([byteArray], {
                                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                    });

                                    const downloadLink = document.createElement('a');
                                    downloadLink.href = window.URL.createObjectURL(blob);
                                    downloadLink.download = `annual-report-${Date.now()}.xlsx`;
                                    downloadLink.click();
                                    window.URL.revokeObjectURL(downloadLink.href);

                                    return fromActions.exportAnnualReportExcelV2.exportAnnualReportExcelV2Success();
                                } else {
                                    throw new Error(payload.error || 'Failed to generate Excel file');
                                }
                            }),
                            catchError((error) => {
                                return of(
                                    fromActions.exportAnnualReportExcelV2.exportAnnualReportExcelV2Failed({
                                        payload: error,
                                    }),
                                );
                            }),
                        );
                    }),
                    catchError((error) => {
                        return of(
                            fromActions.exportAnnualReportExcelV2.exportAnnualReportExcelV2Failed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );


    exportAnnualReportToExcelStructured$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.exportAnnualReportToExcelStructured.exportAnnualReportToExcelStructured),
            mergeMap((action) =>
                this.service.exportAnnualReportToExcelStructured$(action.payload).pipe(
                    map((response) => {
                        const blob = new Blob([response], {
                            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        });
                        const downloadLink = document.createElement('a');
                        downloadLink.href = window.URL.createObjectURL(blob);
                        downloadLink.download = this.generateFilename(action.payload.data, 'xlsx');
                        downloadLink.click();
                        return fromActions.exportAnnualReportToExcelStructured.exportAnnualReportToExcelStructuredSuccess();
                    }),
                    catchError((error: HttpErrorResponse) =>
                        of(
                            fromActions.exportAnnualReportToExcelStructured.exportAnnualReportToExcelStructuredFailed({
                                payload: error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    exportAnnualReportToExcelStructuredV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.exportAnnualReportToExcelStructuredV2.exportAnnualReportToExcelStructuredV2),
            mergeMap((action) =>
                this.service.exportAnnualReportToExcelStructuredV2$(action.payload).pipe(
                    mergeMap(() => {
                        // HTTP 202 accepted, now wait for WebSocket notification
                        return this.notificationsSocketService.printJobCompleted$.pipe(
                            take(1),
                            timeout(60000),
                            map((payload) => {
                                if (payload.success && payload.data) {
                                    // Convert base64 to blob and download
                                    const byteCharacters = atob(payload.data);
                                    const byteNumbers = new Array(byteCharacters.length);
                                    for (let i = 0; i < byteCharacters.length; i++) {
                                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                                    }
                                    const byteArray = new Uint8Array(byteNumbers);
                                    const blob = new Blob([byteArray], {
                                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                    });

                                    const downloadLink = document.createElement('a');
                                    downloadLink.href = window.URL.createObjectURL(blob);
                                    downloadLink.download = this.generateFilename(action.payload.data, 'xlsx');
                                    downloadLink.click();
                                    window.URL.revokeObjectURL(downloadLink.href);

                                    return fromActions.exportAnnualReportToExcelStructuredV2.exportAnnualReportToExcelStructuredV2Success();
                                } else {
                                    throw new Error(payload.error || 'Failed to generate structured Excel file');
                                }
                            }),
                            catchError((error) => {
                                return of(
                                    fromActions.exportAnnualReportToExcelStructuredV2.exportAnnualReportToExcelStructuredV2Failed({
                                        payload: error,
                                    }),
                                );
                            }),
                        );
                    }),
                    catchError((error: HttpErrorResponse) =>
                        of(
                            fromActions.exportAnnualReportToExcelStructuredV2.exportAnnualReportToExcelStructuredV2Failed({
                                payload: error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );



    exportAnnualReportToWord$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.exportAnnualReportToWord.exportAnnualReportToWord.type),
            mergeMap((action: fromActions.ActionWithPayload<PrintUsingPdfTronDto>) => {
                return this.service.exportAnnualReportToWord$(action.payload).pipe(
                    map((response) => {
                        const blob = new Blob([response], {
                            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        });
                        const downloadLink = document.createElement('a');
                        downloadLink.href = window.URL.createObjectURL(blob);
                        downloadLink.download = this.generateFilename(action.payload.data, 'docx');
                        downloadLink.click();
                        return fromActions.exportAnnualReportToWord.exportAnnualReportToWordSuccess();
                    }),
                    catchError((error) => {
                        return of(
                            fromActions.exportAnnualReportToWord.exportAnnualReportToWordFailed({ payload: error }),
                        );
                    }),
                );
            }),
        ),
    );

    exportAnnualReportToWordV2$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.exportAnnualReportToWordV2.exportAnnualReportToWordV2.type),
            mergeMap((action: fromActions.ActionWithPayload<PrintUsingPdfTronDto>) => {
                return this.service.exportAnnualReportToWordV2$(action.payload).pipe(
                    mergeMap(() => {
                        // HTTP 202 accepted, now wait for WebSocket notification
                        return this.notificationsSocketService.printJobCompleted$.pipe(
                            take(1),
                            timeout(60000),
                            map((payload) => {
                                if (payload.success && payload.data) {
                                    // Convert base64 to blob and download
                                    const byteCharacters = atob(payload.data);
                                    const byteNumbers = new Array(byteCharacters.length);
                                    for (let i = 0; i < byteCharacters.length; i++) {
                                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                                    }
                                    const byteArray = new Uint8Array(byteNumbers);
                                    const blob = new Blob([byteArray], {
                                        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                    });

                                    const downloadLink = document.createElement('a');
                                    downloadLink.href = window.URL.createObjectURL(blob);
                                    downloadLink.download = this.generateFilename(action.payload.data, 'docx');
                                    downloadLink.click();
                                    window.URL.revokeObjectURL(downloadLink.href);

                                    return fromActions.exportAnnualReportToWordV2.exportAnnualReportToWordV2Success();
                                } else {
                                    throw new Error(payload.error || 'Failed to generate Word document');
                                }
                            }),
                            catchError((error) => {
                                return of(
                                    fromActions.exportAnnualReportToWordV2.exportAnnualReportToWordV2Failed({ payload: error }),
                                );
                            }),
                        );
                    }),
                    catchError((error) => {
                        return of(
                            fromActions.exportAnnualReportToWordV2.exportAnnualReportToWordV2Failed({ payload: error }),
                        );
                    }),
                );
            }),
        ),
    );



    getPreviousAnnualReportOptions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getPreviousAnnualReportOptions.type),
            mergeMap((payload) => {
                return this.service.getPreviousAnnualReportOptions$(payload).pipe(
                    map((response: Partial<IAnnualReport>[]) => {
                        return fromActions.onGetPreviousAnnualReportOptionsSuccessfully({
                            payload: response,
                        });
                    }),
                    catchError((error) => {
                        return of(fromActions.onGetPreviousAnnualReportOptionsFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getAnnualReportsByCompanyId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getAnnualReportsByCompanyId.type),
            mergeMap((payload) => {
                return this.service.getAnnualReportsByCompanyId$(payload).pipe(
                    map((response: IAnnualReport[]) => {
                        return fromActions.onGetAnnualReportsByCompanyIdSuccessfully({
                            payload: response,
                        });
                    }),
                    catchError((error) => {
                        return of(fromActions.onGetAnnualReportsByCompanyIdFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getActiveAnnualReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getActiveAnnualReport.type),
            mergeMap((payload) => {
                return this.service.getAnnualReportByEncryptedId$(payload).pipe(
                    concatMap((response: IAnnualReport) => {
                        const placeholder = PlaceholderHelper.getPlaceholderMap(
                            response,
                            this.dateLocalePipe,
                            this.finNumberPipe,
                        );
                        return [
                            fromActions.onGetActiveAnnualReportSuccessfully({ payload: response }),
                            fromActions.updateAnnualReportPlaceHolder({ payload: placeholder }),
                        ];
                    }),
                    catchError((error) => {
                        return of(fromActions.onGetActiveAnnualReportFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );
    setActiveAnnualReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.setActiveAnnualReport.type),
            map((payload: IAnnualReport) => {
                const placeholder = PlaceholderHelper.getPlaceholderMap(
                    payload,
                    this.dateLocalePipe,
                    this.finNumberPipe,
                );
                return fromActions.updateAnnualReportPlaceHolder({ payload: placeholder });
            }),
        ),
    );

    getBalanceAutofill$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getBalanceAutofill.type),
            mergeMap((payload) => {
                return this.service.getAnnualReportById$(payload).pipe(
                    concatMap((response: IAnnualReport) => {
                        return [fromActions.onGetBalanceAutofillSuccessfully({ payload: response })];
                    }),
                    catchError((error) => {
                        return of(fromActions.onGetBalanceAutofillFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getIncomeAutofill$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getIncomeAutofill.type),
            mergeMap((payload) => {
                return this.service.getAnnualReportById$(payload).pipe(
                    concatMap((response: IAnnualReport) => {
                        return [fromActions.onGetIncomeAutofillSuccessfully({ payload: response })];
                    }),
                    catchError((error) => {
                        return of(fromActions.onGetIncomeAutofillFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getAnnualReportForMappingAutoFill$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getAnnualReportForMappingAutoFill.type),
            mergeMap((payload) => {
                return this.service.getAnnualReportByEncryptedId$(payload).pipe(
                    concatMap((response: IAnnualReport) => {
                        return [
                            fromActions.onGetAnnualReportForMappingAutoFillSuccessfully({
                                payload: response,
                            }),
                        ];
                    }),
                    catchError((error) => {
                        return of(fromActions.onGetAnnualReportForMappingAutoFillFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getActiveAnnualReports$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getActiveAnnualReports.type),
            mergeMap((payload) => {
                return this.service.getActiveAnnualReports(payload).pipe(
                    map((response: AnnualReportMetaInformationDTO[]) => {
                        debug(response);
                        return fromActions.onGettingActiveAnnualReportsSuccessfully({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.onGettingActiveAnnualReportsFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getAnnualReportsListToCopyFrom$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getAnnualReportsListToCopyFrom.getAnnualReportsListToCopyFrom.type),
            mergeMap(() => {
                return this.service.getActiveAnnualReports({
                    payload: {
                        filter: {
                            onlyWithNotes: true
                        }
                    }
                }).pipe(
                    map((response: AnnualReportMetaInformationDTO[]) => {
                        debug(response);
                        return fromActions.getAnnualReportsListToCopyFrom.getAnnualReportsListToCopyFromSuccess({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.getAnnualReportsListToCopyFrom.getAnnualReportsListToCopyFromFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    getArchivedAnnualReports$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getArchivedAnnualReports.type),
            mergeMap((payload) => {
                return this.service.getArchivedAnnualReports(payload).pipe(
                    map((response: AnnualReportMetaInformationDTO[]) => {
                        debug(response);
                        return fromActions.onGettingArchivedAnnualReportsSuccessfully({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.onGettingArchivedAnnualReportsFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    archiveAnnualReports$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.archiveAnnualReports),
            mergeMap((payload) => {
                return this.service.archiveAnnualReports$(payload).pipe(
                    map(() => {
                        return fromActions.onArchiveAnnualReportsSuccessfully({
                            annualReportIds: payload.annualReportIds,
                        });
                    }),
                    catchError((error) => {
                        return of(fromActions.onArchiveAnnualReportsFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    restoreAnnualReports$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.restoreAnnualReports),
            mergeMap((payload) => {
                return this.service.restoreAnnualReports$(payload).pipe(
                    map(() => {
                        return fromActions.onRestoreAnnualReportsSuccessfully({
                            annualReportIds: payload.annualReportIds,
                        });
                    }),
                    catchError((error) => {
                        return of(fromActions.onRestoreAnnualReportsFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    deleteAnnualReports$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.deleteAnnualReports),
            mergeMap((payload) => {
                return this.service.deleteAnnualReports$(payload).pipe(
                    map(() => {
                        return fromActions.onDeleteAnnualReportsSuccessfully({
                            annualReportIds: payload.annualReportIds,
                        });
                    }),
                    catchError((error) => {
                        return of(fromActions.onDeleteAnnualReportsFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    errorOnAnnualReport$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fromActions.onUpdateAnnualReportFailed.type, fromActions.onCreateAnnualReportFailed.type),
                tap((action: fromActions.ActionWithPayload<HttpErrorResponse>) =>
                    this.handleOnLoadAllErrors(action.payload),
                ),
            ),
        { dispatch: false },
    );

    getAnnualReportToCopyNotesStructureFrom$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getAnnualReportToCopyNotesStructureFrom.type),
            mergeMap((payload) => {
                return this.service.getAnnualReportByEncryptedId$(payload).pipe(
                    concatMap((response: IAnnualReport) => {
                        const notes = response.notes as any;
                        const isNotesNullOrEmpty =
                            notes == null ||
                            notes.accordions == null ||
                            notes.accordions.length === 0;

                        if (isNotesNullOrEmpty) {
                            return [
                                fromActions.onGetAnnualReportToCopyNotesStructureFromFailed({
                                    payload: new HttpErrorResponse({
                                        status: 422,
                                        statusText: 'Notes not found or empty',
                                    }),
                                }),
                            ];
                        }

                        return [
                            fromActions.onGetAnnualReportToCopyNotesStructureFromSuccessfully({
                                payload: response,
                            }),
                        ];
                    }),
                    catchError((error) => {
                        return of(fromActions.onGetAnnualReportToCopyNotesStructureFromFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );
    sealAnnualReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.sealAnnualReport),
            switchMap((action) =>
                this.service.sealAnnualReport$(action.payload).pipe(
                    concatMap(() => [fromActions.onSealAnnualReportSuccessfully()]),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.onSealAnnualReportFailed({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    unsealAnnualReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.unsealAnnualReport),
            switchMap((action) =>
                this.service.unsealAnnualReport$(action.payload).pipe(
                    concatMap(() => [fromActions.onUnsealAnnualReportSuccessfully()]),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.onUnsealAnnualReportFailed({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    updatePrintOptions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updatePrintOptions),
            switchMap((action) =>
                this.service
                    .updatePrintOptions$(action.payload.annualReportEncryptedId, action.payload.printOptions)
                    .pipe(
                        concatMap(() => [
                            fromActions.onUpdatePrintOptionsSuccessfully({
                                payload: { printOptions: action.payload.printOptions },
                            }),
                        ]),
                        catchError((error: HttpErrorResponse) =>
                            of(fromActions.onUpdatePrintOptionsFailed({ payload: error })),
                        ),
                    ),
            ),
        ),
    );

    getAnnualReportAuditLogs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getAnnualReportSealHistory),
            switchMap((action) =>
                this.service.getAnnualReportAuditLogs$(action.payload).pipe(
                    map((response: any[]) =>
                        fromActions.onGetAnnualReportSealHistorySuccessfully({ payload: response }),
                    ),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.onGetAnnualReportSealHistoryFailed({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    getNextBalanceSheetDateForCompanyById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getNextBalanceSheetDateForCompanyById.getNextBalanceSheetDateForCompanyById.type),
            mergeMap((action: fromActions.ActionWithPayload<{ companyId: string; reportType: ReportType }>) => {
                return this.service
                    .getNextBalanceSheetDateForCompanyById$(action.payload.companyId, action.payload.reportType)
                    .pipe(
                        map((response: Date) => {
                            return fromActions.getNextBalanceSheetDateForCompanyById.getNextBalanceSheetDateForCompanyByIdSuccess(
                                {
                                    payload: response,
                                },
                            );
                        }),
                        catchError((error) => {
                            return of(
                                fromActions.getNextBalanceSheetDateForCompanyById.getNextBalanceSheetDateForCompanyByIdFailed(
                                    { payload: error },
                                ),
                            );
                        }),
                    );
            }),
        ),
    );

    handleOnLoadAllErrors(error) {
        debug('handleOnLoadAllErrors');
        debug(error);
        return error;
    }

    // Bulk create financial year effects
    bulkCreateFinancialYearPreview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.bulkCreateFinancialYearPreview),
            switchMap((action) =>
                this.service.bulkCreateFinancialYearPreview$(action.payload).pipe(
                    map((response) =>
                        fromActions.onBulkCreateFinancialYearPreviewSuccessfully({ payload: response }),
                    ),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.onBulkCreateFinancialYearPreviewFailed({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    bulkCreateFinancialYear$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.bulkCreateFinancialYear),
            switchMap((action) =>
                this.service.bulkCreateFinancialYear$(action.payload).pipe(
                    map((response) => fromActions.onBulkCreateFinancialYearSuccessfully({ payload: response })),
                    catchError((error: HttpErrorResponse) =>
                        of(fromActions.onBulkCreateFinancialYearFailed({ payload: error })),
                    ),
                ),
            ),
        ),
    );

    // Start polling when job is successfully created
    startPollingOnJobCreation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.onBulkCreateFinancialYearSuccessfully),
            map((action) => fromActions.startPollingBulkCreateStatus({ jobId: action.payload.jobId })),
        ),
    );

    // Polling effect - polls every 1 second
    pollBulkCreateStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.startPollingBulkCreateStatus),
            switchMap((action) =>
                interval(1000).pipe(
                    // Stop polling when stopPollingBulkCreateStatus is dispatched
                    takeUntil(this.actions$.pipe(ofType(fromActions.stopPollingBulkCreateStatus))),
                    // Poll the status endpoint
                    switchMap(() =>
                        this.service.getBulkCreateFinancialYearStatus$(action.jobId).pipe(
                            map((status) => fromActions.onBulkCreateStatusUpdate({ payload: status })),
                            catchError((error: HttpErrorResponse) => {
                                // If 404, job not found - stop polling
                                if (error.status === 404) {
                                    return of(fromActions.stopPollingBulkCreateStatus());
                                }
                                // For other errors, continue polling (temporary network issues)
                                return EMPTY;
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    // Handle status updates and detect completion
    handleStatusUpdate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.onBulkCreateStatusUpdate),
            switchMap((action) => {
                const status = action.payload.status;

                if (status === 'completed') {
                    return of(
                        fromActions.onBulkCreateJobCompleted({ payload: action.payload }),
                        fromActions.stopPollingBulkCreateStatus(),
                    );
                } else if (status === 'failed') {
                    return of(
                        fromActions.onBulkCreateJobFailed({ payload: action.payload }),
                        fromActions.stopPollingBulkCreateStatus(),
                    );
                }

                // Continue polling for 'pending' and 'processing' statuses
                return EMPTY;
            }),
        ),
    );
}
